import { RibbonGroupBase } from '../ribbon-interfaces';
import { RibbonItemSize } from '@syncfusion/ej2-ribbon';
import { createElement, getInstance, isNullOrUndefined } from '@syncfusion/ej2-base';
import { FontHelper } from '../../helper/font-helper';
import { ComboBox } from '@syncfusion/ej2-dropdowns';
// Font group constants
export const FONT_GROUP_ID = '_font_group';
export const FONT_FAMILY_ID = '_font_family';
export const FONT_SIZE_ID = '_font_size';
export const BOLD_ID = '_bold';
export const ITALIC_ID = '_italic';
export const UNDERLINE_ID = '_underline';
export const STRIKETHROUGH_ID = '_strikethrough';
export const SUPERSCRIPT_ID = '_superscript';
export const SUBSCRIPT_ID = '_subscript';
export const FONT_COLOR_ID = '_font_color';
export const HIGHLIGHT_COLOR_ID = '_highlight_color';
export const CLEAR_FORMAT_ID = '_clear_format';
export const GROW_FONT_ID = '_grow_format';
export const SHRINK_FONT_ID = '_shrink_format';
export const CHANGE_CASE_ID = '_change_case';
/**
 * FontGroup class for handling font formatting operations in Document Editor ribbon
 * @private
 */
export class FontGroup extends RibbonGroupBase {
    constructor() {
        super(...arguments);
        this.appliedHighlightColor = 'rgb(255, 255, 0)';
        this.highlightColorHandlers = [];
    }
    /**
     * Get the Ribbon group model for Font formatting
     * @returns {RibbonGroupModel} - Ribbon group model for Font formatting
     * @private
     */
    getGroupModel() {
        const id = this.ribbonId;
        return {
            id: id + FONT_GROUP_ID,
            cssClass: 'e-font-group',
            groupIconCss: 'e-icons e-de-ctnr-fontcolor',
            header: this.localObj.getConstant('Font'),
            isCollapsible: false,
            enableGroupOverflow: true,
            overflowHeader: this.localObj.getConstant('More Font Options'),
            orientation: 'Row',
            showLauncherIcon: true,
            launcherIconKeyTip: 'FG',
            collections: [
                {
                    id: id + '_font-collection',
                    items: [
                        {
                            type: 'ComboBox',
                            keyTip: 'FF',
                            allowedSizes: RibbonItemSize.Small,
                            comboBoxSettings: {
                                dataSource: this.container.documentEditorSettings.fontFamilies,
                                label: this.localObj.getConstant('Font'),
                                width: '150px',
                                popupHeight: '250px',
                                popupWidth: '150px',
                                cssClass: 'e-de-prop-dropdown',
                                allowFiltering: true,
                                change: () => {
                                    this.changeFontFamily();
                                }
                            },
                            id: id + FONT_FAMILY_ID,
                            ribbonTooltipSettings: { content: this.localObj.getConstant('Font') }
                        },
                        {
                            type: 'ComboBox',
                            keyTip: 'FS',
                            allowedSizes: RibbonItemSize.Small,
                            comboBoxSettings: {
                                label: this.localObj.getConstant('Font Size'),
                                dataSource: ['8', '9', '10', '11', '12', '14', '16', '18', '20', '22', '24', '26', '28', '36', '48', '72', '96'],
                                width: '65px',
                                cssClass: 'e-de-prop-dropdown',
                                showClearButton: false,
                                change: () => {
                                    this.changeFontSize();
                                }
                            },
                            id: id + FONT_SIZE_ID,
                            ribbonTooltipSettings: { content: this.localObj.getConstant('Font Size') }
                        }, {
                            type: 'Button',
                            keyTip: 'FG',
                            allowedSizes: RibbonItemSize.Small,
                            buttonSettings: {
                                content: this.localObj.getConstant('Grow Font Size'),
                                iconCss: 'e-icons e-de-ctnr-increase-fontsize',
                                clicked: () => {
                                    this.increaseFontSize();
                                }
                            },
                            id: id + GROW_FONT_ID,
                            ribbonTooltipSettings: { content: this.localObj.getConstant('Grow Font Size') }
                        },
                        {
                            type: 'Button',
                            keyTip: 'FK',
                            allowedSizes: RibbonItemSize.Small,
                            buttonSettings: {
                                content: this.localObj.getConstant('Shrink Font Size'),
                                iconCss: 'e-icons e-de-ctnr-decrease-fontsize',
                                clicked: () => {
                                    this.decreaseFontSize();
                                }
                            },
                            id: id + SHRINK_FONT_ID,
                            ribbonTooltipSettings: { content: this.localObj.getConstant('Shrink Font Size') }
                        }, {
                            type: 'DropDown',
                            keyTip: 'CC',
                            allowedSizes: RibbonItemSize.Small,
                            dropDownSettings: {
                                iconCss: 'e-icons e-de-ctnr-change-case',
                                content: this.localObj.getConstant('Change case Tooltip'),
                                items: FontHelper.getChangeCaseItems(this.localObj, id),
                                select: (args) => {
                                    this.changeCaseAction(args);
                                }
                            },
                            id: id + CHANGE_CASE_ID,
                            ribbonTooltipSettings: { content: this.localObj.getConstant('Change case Tooltip') }
                        }
                    ]
                }, {
                    items: [{
                            type: 'Button',
                            keyTip: '1',
                            allowedSizes: RibbonItemSize.Small,
                            buttonSettings: {
                                content: this.localObj.getConstant('Bold Tooltip'),
                                cssClass: 'e-de-icon-Bold',
                                iconCss: 'e-icons e-de-ctnr-bold',
                                isToggle: true,
                                clicked: () => {
                                    this.boldAction();
                                }
                            },
                            id: id + BOLD_ID,
                            ribbonTooltipSettings: { content: this.localObj.getConstant('Bold Tooltip') }
                        },
                        {
                            type: 'Button',
                            keyTip: '2',
                            allowedSizes: RibbonItemSize.Small,
                            buttonSettings: {
                                content: this.localObj.getConstant('Italic Tooltip'),
                                cssClass: 'e-de-icon-Italic',
                                iconCss: 'e-icons e-de-ctnr-italic',
                                isToggle: true,
                                clicked: () => {
                                    this.italicAction();
                                }
                            },
                            id: id + ITALIC_ID,
                            ribbonTooltipSettings: { content: this.localObj.getConstant('Italic Tooltip') }
                        },
                        {
                            type: 'Button',
                            keyTip: '3',
                            allowedSizes: RibbonItemSize.Small,
                            buttonSettings: {
                                content: this.localObj.getConstant('Underline Tooltip'),
                                cssClass: 'e-de-icon-Underline',
                                iconCss: 'e-icons e-de-ctnr-underline',
                                isToggle: true,
                                clicked: () => {
                                    this.underlineAction();
                                }
                            },
                            id: id + UNDERLINE_ID,
                            ribbonTooltipSettings: { content: this.localObj.getConstant('Underline Tooltip') }
                        },
                        {
                            type: 'Button',
                            keyTip: '4',
                            allowedSizes: RibbonItemSize.Small,
                            buttonSettings: {
                                content: this.localObj.getConstant('Strikethrough'),
                                cssClass: 'e-de-icon-Strikethrough',
                                iconCss: 'e-icons e-de-ctnr-strikethrough',
                                isToggle: true,
                                clicked: () => {
                                    this.strikethroughAction();
                                }
                            },
                            id: id + STRIKETHROUGH_ID,
                            ribbonTooltipSettings: { content: this.localObj.getConstant('Strikethrough') }
                        },
                        {
                            type: 'Button',
                            keyTip: '5',
                            allowedSizes: RibbonItemSize.Small,
                            buttonSettings: {
                                content: this.localObj.getConstant('Superscript Tooltip'),
                                cssClass: 'e-de-icon-Superscript',
                                iconCss: 'e-icons e-de-ctnr-superscript',
                                isToggle: true,
                                clicked: () => {
                                    this.superscriptAction();
                                }
                            },
                            id: id + SUPERSCRIPT_ID,
                            ribbonTooltipSettings: { content: this.localObj.getConstant('Superscript Tooltip') }
                        },
                        {
                            type: 'Button',
                            keyTip: '6',
                            allowedSizes: RibbonItemSize.Small,
                            buttonSettings: {
                                content: this.localObj.getConstant('Subscript Tooltip'),
                                cssClass: 'e-de-icon-Subscript',
                                iconCss: 'e-icons e-de-ctnr-subscript',
                                isToggle: true,
                                clicked: () => {
                                    this.subscriptAction();
                                }
                            },
                            id: id + SUBSCRIPT_ID,
                            ribbonTooltipSettings: { content: this.localObj.getConstant('Subscript Tooltip') }
                        },
                        {
                            type: 'ColorPicker',
                            keyTip: 'I',
                            allowedSizes: RibbonItemSize.Small,
                            colorPickerSettings: {
                                label: this.localObj.getConstant('Font color'),
                                htmlAttributes: { 'aria-label': this.localObj.getConstant('Font color') },
                                value: '#000000',
                                change: (args) => {
                                    this.changeFontColor(args);
                                }
                            },
                            id: id + FONT_COLOR_ID,
                            ribbonTooltipSettings: { content: this.localObj.getConstant('Font color') }
                        },
                        {
                            type: 'SplitButton',
                            keyTip: 'FC',
                            allowedSizes: RibbonItemSize.Small,
                            splitButtonSettings: this.createHighlightColorSplitButton(),
                            id: id + HIGHLIGHT_COLOR_ID,
                            ribbonTooltipSettings: { content: this.localObj.getConstant('Text highlight color') }
                        },
                        {
                            type: 'Button',
                            keyTip: 'E',
                            allowedSizes: RibbonItemSize.Small,
                            buttonSettings: {
                                content: this.localObj.getConstant('Clear all formatting'),
                                iconCss: 'e-icons e-de-ctnr-clearall',
                                clicked: () => {
                                    this.clearFormatAction();
                                }
                            },
                            id: id + CLEAR_FORMAT_ID,
                            ribbonTooltipSettings: { content: this.localObj.getConstant('Clear all formatting') }
                        }
                    ]
                }
            ]
        };
    }
    /**
     * Update font UI based on selection state
     * @returns {void}
     * @private
     */
    updateSelection() {
        // Get the selection character format
        if (!this.documentEditor.selection) {
            return;
        }
        const characterFormat = this.documentEditor.selection.characterFormat;
        const id = this.ribbonId;
        // Update toggle buttons
        this.updateToggleButtonState(id + BOLD_ID, characterFormat.bold);
        this.updateToggleButtonState(id + ITALIC_ID, characterFormat.italic);
        this.updateToggleButtonState(id + UNDERLINE_ID, characterFormat.underline !== 'None');
        this.updateToggleButtonState(id + STRIKETHROUGH_ID, characterFormat.strikethrough !== 'None');
        this.updateToggleButtonState(id + SUPERSCRIPT_ID, characterFormat.baselineAlignment === 'Superscript');
        this.updateToggleButtonState(id + SUBSCRIPT_ID, characterFormat.baselineAlignment === 'Subscript');
        // Update font family dropdown
        if (characterFormat.fontFamily) {
            let fontFamily;
            if (!isNullOrUndefined(characterFormat.renderedFontFamily) &&
                !isNullOrUndefined(characterFormat.fontFamily)) {
                fontFamily = characterFormat.renderedFontFamily;
            }
            else {
                fontFamily = characterFormat.fontFamily;
            }
            const fontFamilyElement = document.getElementById(id + FONT_FAMILY_ID);
            if (fontFamilyElement) {
                fontFamilyElement.value = fontFamily;
            }
        }
        else {
            const fontFamilyElement = document.getElementById(id + FONT_FAMILY_ID);
            if (fontFamilyElement) {
                const fontFamilyInstance = getInstance(fontFamilyElement, ComboBox);
                fontFamilyInstance.value = null;
                fontFamilyElement.value = null;
            }
        }
        // Update font size dropdown
        if (characterFormat.fontSize) {
            const fontSize = characterFormat.fontSize.toString();
            const fontSizeElement = document.getElementById(id + FONT_SIZE_ID);
            if (fontSizeElement) {
                fontSizeElement.value = fontSize;
            }
        }
        else {
            const fontSizeElement = document.getElementById(id + FONT_SIZE_ID);
            if (fontSizeElement) {
                const fontSizeInstance = getInstance(fontSizeElement, ComboBox);
                fontSizeInstance.value = null;
                fontSizeElement.value = null;
            }
        }
        // Update highlight color button
        if (characterFormat.highlightColor) {
            const highlightColor = document.getElementById(id + HIGHLIGHT_COLOR_ID);
            if (highlightColor) {
                const highlightColorElement = highlightColor.querySelector('.e-split-btn-icon');
                if (highlightColorElement) {
                    highlightColorElement.style.backgroundColor = this.getBackgroundColorFromHighlightColor(characterFormat.highlightColor);
                }
            }
        }
        // Update font color button
        if (characterFormat.fontColor) {
            let fontColor = characterFormat.fontColor;
            if (fontColor === 'empty' || fontColor === '#00000000') {
                fontColor = '#000000';
            }
            const fontColorElement = document.getElementById(id + FONT_COLOR_ID);
            if (fontColorElement) {
                const fontColorInput = fontColorElement.querySelector('input');
                if (fontColorInput) {
                    fontColorInput.value = fontColor;
                }
            }
        }
    }
    updateToggleButtonState(buttonId, isActive) {
        // Use Ribbon's getItem method for better performance
        const ribbonObj = this.container.ribbon.ribbon;
        if (ribbonObj) {
            const button = document.getElementById(buttonId);
            if (button) {
                if (isActive) {
                    button.classList.add('e-active');
                    button.setAttribute('aria-pressed', 'true');
                }
                else {
                    button.classList.remove('e-active');
                    button.setAttribute('aria-pressed', 'false');
                }
            }
        }
    }
    getHighlightColorItems() {
        return FontHelper.getHighlightColorItems(this.localObj, this.ribbonId);
    }
    createHighlightColorSplitButton() {
        let highlightIconCss = 'e-de-ctnr-highlight e-icons';
        if (this.isRtl) {
            highlightIconCss += ' e-de-flip';
        }
        const colorListDropDiv = createElement('div', {
            id: this.ribbonId + '_color_list_div',
            styles: 'visibility: hidden'
        });
        // Create the HTML template for highlight color dropdown
        const highlightColorElement = createElement('ul', {
            id: this.ribbonId + '_ribbon_highlight_color',
            styles: 'visibility: visible; display: grid; grid-template-columns: repeat(5,1fr); padding: 2px 2px; box-shadow: none'
        });
        colorListDropDiv.appendChild(highlightColorElement);
        const highlightColors = this.getHighlightColorItems();
        highlightColors.forEach((color) => {
            const colorDiv = createElement('li', { className: 'e-de-ctnr-hglt-btn' });
            colorDiv.style.backgroundColor = color.backgroundColor;
            highlightColorElement.appendChild(colorDiv);
            // Create bound handler and store reference
            const handler = this.onHighlightColorClick.bind(this, color.backgroundColor);
            colorDiv.addEventListener('click', handler);
            this.highlightColorHandlers.push({ element: colorDiv, handler });
        });
        const noColorList = createElement('ul', { styles: 'visibility: visible; padding:2px' });
        const noColorListItem = createElement('li', { className: 'e-hglt-no-color' });
        colorListDropDiv.appendChild(noColorList);
        noColorList.appendChild(noColorListItem);
        const noColorDiv = createElement('div', { styles: 'width:24px;height:24px;background-color:#ffffff;margin:3px;', id: 'noColorDiv' });
        noColorListItem.appendChild(noColorDiv);
        const noColorLabel = createElement('div', { innerHTML: this.localObj.getConstant('No color'), className: 'e-de-ctnr-hglt-no-color' });
        noColorDiv.appendChild(noColorLabel);
        noColorDiv.addEventListener('click', this.onHighlightColorClick.bind(this, 'transparent'));
        // Create bound handler and store reference
        const noColorHandler = this.onHighlightColorClick.bind(this, 'transparent');
        noColorDiv.addEventListener('click', noColorHandler);
        this.highlightColorHandlers.push({ element: noColorDiv, handler: noColorHandler });
        return {
            target: colorListDropDiv,
            iconCss: highlightIconCss,
            content: this.localObj.getConstant('Text highlight color'),
            // cssClass: this.splitButtonClass,
            htmlAttributes: { 'aria-label': this.localObj.getConstant('Text highlight color') },
            items: this.getHighlightColorItems().map((color) => ({ id: color.id, text: color.text })),
            select: (args) => {
                this.applyHighlightColor(args.item.backgroundColor);
            },
            beforeOpen: () => {
                colorListDropDiv.style.visibility = 'visible';
            },
            beforeClose: () => {
                colorListDropDiv.style.visibility = 'hidden';
            },
            click: () => {
                this.applyHighlightColor(this.appliedHighlightColor);
            }
        };
    }
    onHighlightColorClick(color, event) {
        this.applyHighlightColor(color);
        // Remove the 'e-color-selected' class from all children
        const container = event.currentTarget.closest('#' + this.ribbonId + '_color_list_div')
            || document.getElementById(this.ribbonId + '_color_list_div')
            || event.currentTarget.parentElement;
        if (container) {
            container.querySelectorAll('.e-color-selected').forEach(function (el) {
                el.classList.remove('e-color-selected');
            });
        }
        // Add the 'e-color-selected' class to the clicked element
        event.currentTarget.classList.add('e-color-selected');
    }
    applyHighlightColor(color) {
        this.appliedHighlightColor = FontHelper.applyHighlightColor(this.documentEditor, color);
    }
    getBackgroundColorFromHighlightColor(highlightColor) {
        return FontHelper.getBackgroundColorFromHighlightColor(highlightColor);
    }
    boldAction() {
        FontHelper.applyFontFormatting(this.documentEditor, 'bold');
    }
    italicAction() {
        FontHelper.applyFontFormatting(this.documentEditor, 'italic');
    }
    underlineAction() {
        FontHelper.applyFontFormatting(this.documentEditor, 'underline');
    }
    strikethroughAction() {
        FontHelper.applyFontFormatting(this.documentEditor, 'strikethrough');
    }
    superscriptAction() {
        FontHelper.applyFontFormatting(this.documentEditor, 'superscript');
    }
    subscriptAction() {
        FontHelper.applyFontFormatting(this.documentEditor, 'subscript');
    }
    changeCaseAction(args) {
        FontHelper.applyChangeCase(this.documentEditor, this.localObj, args.item.text);
    }
    changeFontFamily() {
        const fontFamilyElement = document.getElementById(this.ribbonId + FONT_FAMILY_ID);
        if (fontFamilyElement) {
            const fontFamily = fontFamilyElement.value;
            FontHelper.changeFontFamily(this.documentEditor, fontFamily);
        }
    }
    changeFontSize() {
        const fontSizeElement = document.getElementById(this.ribbonId + FONT_SIZE_ID);
        if (fontSizeElement) {
            const fontSize = fontSizeElement.value;
            FontHelper.changeFontSize(this.documentEditor, fontSize);
        }
    }
    changeFontColor(args) {
        FontHelper.changeFontColor(this.documentEditor, args.currentValue.hex);
    }
    clearFormatAction() {
        FontHelper.applyFontFormatting(this.documentEditor, 'clearFormat');
    }
    increaseFontSize() {
        FontHelper.increaseFontSize(this.documentEditor);
    }
    decreaseFontSize() {
        FontHelper.decreaseFontSize(this.documentEditor);
    }
    destroy() {
        // Remove highlight color event listeners
        this.highlightColorHandlers.forEach((item) => {
            if (item.element && item.handler) {
                item.element.removeEventListener('click', item.handler);
            }
        });
        this.highlightColorHandlers = [];
    }
}
